import React, { useState, useEffect } from "react";
import { createHomonym, updateHomonym, fetchHomonymById } from "./adminHomonymService";
import ImagePickerModal from "../../media_images/ImagePickerModal";
import "../../styles/content.css";

const ROOT = process.env.REACT_APP_BASE_URL_ROOT;
const toPublicImgSrc = (url) => {
  if (!url) return "";
  const pub = url.replace(/\/api(?:\/admin)?\/media-images\//, "/api/public/media-images/");
  const src = pub.startsWith("http") ? pub : `${ROOT}${pub}`;
  console.log("[HomonymForm] img raw:", url, "→ resolved:", src);
  return src;
};

const defaultMeaning = { meaning: "", context: "", exampleSentence: "", sortOrder: "", imageUrl: "" };

const defaultForm = {
  word: "", ipaPronunciation: "", phoneticPronunciation: "",
  sortOrder: "", youtubeLink: "", imageUrl: "", meanings: [{ ...defaultMeaning }],
};

const HomonymForm = ({ homonym, onSaved, onCancel }) => {
  const isEdit = !!homonym;
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [pickerOpenForMeaning, setPickerOpenForMeaning] = useState(null);

  useEffect(() => {
    if (homonym?.id) {
      fetchHomonymById(homonym.id).then((res) => {
        if (res.status === 200 && res.data) {
          const d = res.data;
          setForm({
            word: d.word || "",
            ipaPronunciation: d.ipaPronunciation || "",
            phoneticPronunciation: d.phoneticPronunciation || "",
            sortOrder: d.sortOrder ?? "",
            youtubeLink: d.youtubeLink || "",
            imageUrl: d.imageUrl || "",
            meanings: d.meanings?.length
              ? d.meanings.map((m) => ({ meaning: m.meaning || "", context: m.context || "", exampleSentence: m.exampleSentence || "", sortOrder: m.sortOrder ?? "", imageUrl: m.imageUrl || "" }))
              : [{ ...defaultMeaning }],
          });
        }
      });
    }
  }, [homonym]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleMeaningChange = (idx, e) => {
    const updated = [...form.meanings];
    updated[idx] = { ...updated[idx], [e.target.name]: e.target.value };
    setForm({ ...form, meanings: updated });
  };

  const updateMeaningField = (idx, field, value) => {
    const updated = [...form.meanings];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, meanings: updated });
  };

  const addMeaning = () => setForm({ ...form, meanings: [...form.meanings, { ...defaultMeaning }] });

  const removeMeaning = (idx) => {
    if (form.meanings.length <= 1) return;
    setForm({ ...form, meanings: form.meanings.filter((_, i) => i !== idx) });
  };

  const handleSubmit = async () => {
    if (!form.word) { alert("Word is required"); return; }
    if (!form.meanings.some((m) => m.meaning.trim())) { alert("At least one meaning is required"); return; }

    const payload = {
      ...form,
      sortOrder: form.sortOrder !== "" ? Number(form.sortOrder) : null,
      youtubeLink: form.youtubeLink || null,
      imageUrl: form.imageUrl || null,
      meanings: form.meanings
        .filter((m) => m.meaning.trim())
        .map((m, i) => ({ ...m, sortOrder: m.sortOrder !== "" ? Number(m.sortOrder) : i + 1, imageUrl: m.imageUrl || null })),
    };

    setLoading(true);
    const res = isEdit ? await updateHomonym(homonym.id, payload) : await createHomonym(payload);
    if (res.status === 200 || res.status === 201) { onSaved(); }
    else { alert(res.message || "Operation failed"); }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ width: 650, maxHeight: "90vh", overflowY: "auto" }}>
        <div className="form-card" style={{ margin: 0, boxShadow: "none" }}>
          <h2>{isEdit ? "Edit" : "Create"} Homonym</h2>

          <div className="form-group"><label>Word *</label><input name="word" placeholder="e.g. Draw" value={form.word} onChange={handleChange} /></div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}><label>IPA Pronunciation</label><input name="ipaPronunciation" placeholder="/drɔː/" value={form.ipaPronunciation} onChange={handleChange} /></div>
            <div className="form-group" style={{ flex: 1 }}><label>Phonetic Pronunciation</label><input name="phoneticPronunciation" placeholder="draw" value={form.phoneticPronunciation} onChange={handleChange} /></div>
          </div>

          <div style={{ display: "flex", gap: 12 }}>
            <div className="form-group" style={{ flex: 1 }}><label>Sort Order</label><input name="sortOrder" type="number" value={form.sortOrder} onChange={handleChange} /></div>
            <div className="form-group" style={{ flex: 1 }}><label>YouTube Link</label><input name="youtubeLink" placeholder="https://youtube.com/..." value={form.youtubeLink} onChange={handleChange} /></div>
          </div>

          <div className="form-group">
            <label>Image (optional)</label>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {form.imageUrl ? (
                <img src={toPublicImgSrc(form.imageUrl)} alt="selected"
                  style={{ width: 60, height: 60, objectFit: "contain", background: "#f5f5f5", borderRadius: 8, border: "1px solid #ddd" }} />
              ) : (
                <div style={{ width: 60, height: 60, background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#aaa" }}>No image</div>
              )}
              <button type="button" className="btn secondary" onClick={() => setShowImagePicker(true)}>Pick Image</button>
              {form.imageUrl && (
                <button type="button" className="btn secondary" onClick={() => setForm({ ...form, imageUrl: "" })}>Clear</button>
              )}
            </div>
          </div>

          <h3 style={{ marginTop: 16 }}>Meanings</h3>
          {form.meanings.map((m, idx) => (
            <div key={idx} style={{ border: "1px solid #ddd", borderRadius: 8, padding: 12, marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <strong>Meaning #{idx + 1}</strong>
                {form.meanings.length > 1 && (
                  <button type="button" onClick={() => removeMeaning(idx)} style={{ color: "red", background: "none", border: "none", cursor: "pointer" }}>✕ Remove</button>
                )}
              </div>
              <div className="form-group"><label>Meaning *</label><input name="meaning" placeholder="e.g. To pull something" value={m.meaning} onChange={(e) => handleMeaningChange(idx, e)} /></div>
              <div className="form-group"><label>Context</label><input name="context" placeholder="e.g. Physical action" value={m.context} onChange={(e) => handleMeaningChange(idx, e)} /></div>
              <div className="form-group"><label>Example Sentence</label><input name="exampleSentence" placeholder="e.g. Draw the curtains please." value={m.exampleSentence} onChange={(e) => handleMeaningChange(idx, e)} /></div>
              <div className="form-group"><label>Sort Order</label><input name="sortOrder" type="number" value={m.sortOrder} onChange={(e) => handleMeaningChange(idx, e)} /></div>
              <div className="form-group">
                <label>Image (optional)</label>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  {m.imageUrl ? (
                    <img src={toPublicImgSrc(m.imageUrl)} alt="meaning"
                      style={{ width: 60, height: 60, objectFit: "contain", background: "#f5f5f5", borderRadius: 8, border: "1px solid #ddd" }} />
                  ) : (
                    <div style={{ width: 60, height: 60, background: "#f0f0f0", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#aaa" }}>No image</div>
                  )}
                  <button type="button" className="btn secondary" onClick={() => setPickerOpenForMeaning(idx)}>Pick Image</button>
                  {m.imageUrl && (
                    <button type="button" className="btn secondary" onClick={() => updateMeaningField(idx, "imageUrl", "")}>Remove</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addMeaning} style={{ marginBottom: 16 }}>+ Add Meaning</button>

          <div className="form-actions">
            <button onClick={handleSubmit} disabled={loading}>{loading ? "Saving..." : isEdit ? "Update" : "Create"}</button>
            <button onClick={onCancel} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>

      <ImagePickerModal
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        onSelect={(url) => { setForm({ ...form, imageUrl: url }); setShowImagePicker(false); }}
        defaultImageUse="GENERAL"
      />

      <ImagePickerModal
        isOpen={pickerOpenForMeaning !== null}
        onClose={() => setPickerOpenForMeaning(null)}
        onSelect={(url) => { updateMeaningField(pickerOpenForMeaning, "imageUrl", url); setPickerOpenForMeaning(null); }}
        defaultImageUse="GENERAL"
      />
    </div>
  );
};

export default HomonymForm;
